import axios from 'axios';

// Configuración base de axios
const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3000',
  timeout: Number(process.env.REACT_APP_API_TIMEOUT) || 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para requests (agregar token de autenticación cuando esté disponible)
API.interceptors.request.use(
  (config) => {
    // Aquí podemos agregar el token de autenticación más adelante
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Control para evitar múltiples refresh simultáneos
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
};

// Interceptor para responses (manejo de errores con refresh automático)
API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Si es 401 y no es un retry, intentar refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Ya hay un refresh en curso, encolar este request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return API(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const token = localStorage.getItem('authToken');
      if (!token) {
        isRefreshing = false;
        localStorage.removeItem('user');
        window.location.reload();
        return Promise.reject(error);
      }

      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/auth/refresh`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const newToken = response.data.access_token;
        localStorage.setItem('authToken', newToken);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        processQueue(null, newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return API(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        window.location.reload();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Servicios de habitaciones
export const roomService = {
  // Obtener todas las habitaciones
  getAllRooms: async () => {
    try {
      const response = await API.get('/room');
      return response.data;
    } catch (error) {
      throw new Error('Error al obtener las habitaciones: ' + error.message);
    }
  },

  // Obtener habitaciones por tipo (HOTEL o GLAMPING)
  getRoomsByType: async (type) => {
    try {
      const response = await API.get(`/room/type/${type}`);
      return response.data;
    } catch (error) {
      throw new Error(`Error al obtener habitaciones de tipo ${type}: ` + error.message);
    }
  },

  // Obtener una habitación por ID
  getRoomById: async (id) => {
    try {
      const response = await API.get(`/room/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(`Error al obtener la habitación ${id}: ` + error.message);
    }
  },
};

// Servicios de reservas
export const reservationService = {
  // Verificar disponibilidad
  checkAvailability: async (checkIn, checkOut, adults, children) => {
    try {
      const params = new URLSearchParams({
        checkIn: checkIn.toISOString().split('T')[0], // Formato YYYY-MM-DD
        checkOut: checkOut.toISOString().split('T')[0],
        adults: adults.toString(),
        children: children.toString(),
      });

      const response = await API.get(`/reservation/availability?${params}`);
      return response.data;
    } catch (error) {
      throw new Error('Error al verificar disponibilidad: ' + error.message);
    }
  },

  // Crear una nueva reserva
  createReservation: async (reservationData) => {
    try {
      const response = await API.post('/reservation', reservationData);
      return response.data;
    } catch (error) {
      throw new Error('Error al crear la reserva: ' + error.message);
    }
  },

  // Obtener todas las reservas
  getAllReservations: async () => {
    try {
      const response = await API.get('/reservation');
      return response.data;
    } catch (error) {
      throw new Error('Error al obtener las reservas: ' + error.message);
    }
  },

  // Obtener una reserva por ID
  getReservationById: async (id) => {
    try {
      const response = await API.get(`/reservation/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(`Error al obtener la reserva ${id}: ` + error.message);
    }
  },
};

// Servicios de autenticación (para implementar más adelante)
export const authService = {
  // Login de usuario
  login: async (email, password) => {
    try {
      const response = await API.post('/auth/login', { email, password });
      if (response.data.access_token) {
        localStorage.setItem('authToken', response.data.access_token);
      }
      return response.data;
    } catch (error) {
      throw new Error('Error al iniciar sesión: ' + error.message);
    }
  },

  // Registro de usuario
  register: async (userData) => {
    try {
      const response = await API.post('/auth/register', userData);
      return response.data;
    } catch (error) {
      throw new Error('Error al registrar usuario: ' + error.message);
    }
  },

  // Logout
  logout: () => {
    localStorage.removeItem('authToken');
  },

  // Verificar si el usuario está autenticado
  isAuthenticated: () => {
    return localStorage.getItem('authToken') !== null;
  },
};

// Exportar la instancia de axios por si se necesita para casos específicos
export default API;